import React from "react";
import {
  Grid,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  IconButton,
  Button,
} from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import PremiumInput from "../../../../Components/Inputs/PremiumInput";
import PremiumSelect from "../../../../Components/Inputs/PremiumSelect";

const LANGUAGE_LEVEL_OPTIONS = [
  { value: "native", label: "زبان مادری" },
  { value: "beginner", label: "مبتدی" },
  { value: "intermediate", label: "متوسط" },
  { value: "upper_intermediate", label: "بالاتر از متوسط" },
  { value: "advanced", label: "پیشرفته" },
];

const SUB_SKILL_OPTIONS = LANGUAGE_LEVEL_OPTIONS.filter(
  (opt) => opt?.value !== "native",
);

export default function LanguageSection({ onAdd, onRemove, formik }) {
  const languages = formik?.values?.languages ?? [];

  return (
    <Box sx={{ mt: 3, p: 2, bgcolor: "rgba(0,0,0,0.2)", borderRadius: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <LanguageIcon sx={{ color: "#00ffcc" }} />
        <Typography sx={{ color: "#fff", fontWeight: 700 }}>
          مهارت‌های زبانی (حداقل یک زبان اختیاری/پیش‌فرض)
        </Typography>
      </Box>

      {languages?.length === 0 ? (
        <Box
          sx={{
            p: 3,
            mb: 2,
            bgcolor: "rgba(255,255,255,0.03)",
            borderRadius: 2,
            border: "1px dashed rgba(255,255,255,0.1)",
            textAlign: "center",
          }}
        >
          <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: 14 }}>
            زبانی ثبت نشده است. برای افزودن زبان روی دکمه زیر کلیک کنید.
          </Typography>
        </Box>
      ) : (
        languages.map((lang, index) => {
          const isNative = lang?.overall_level === "native";

          return (
            <Box
              key={index}
              sx={{
                mb: 3,
                p: 2,
                bgcolor: "rgba(255,255,255,0.05)",
                borderRadius: 2,
              }}
            >
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <PremiumInput
                    name={`languages[${index}].language_name`}
                    label="نام زبان"
                    isRequired
                    formik={formik}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <PremiumSelect
                    name={`languages[${index}].overall_level`}
                    label="سطح کلی تسلط"
                    options={LANGUAGE_LEVEL_OPTIONS}
                    isRequired
                    formik={formik}
                  />
                </Grid>

                <Grid size={{ xs: 6, md: 3 }}>
                  <PremiumSelect
                    name={`languages[${index}].reading_level`}
                    label="مهارت خواندن"
                    options={SUB_SKILL_OPTIONS}
                    disabled={isNative}
                    formik={formik}
                  />
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <PremiumSelect
                    name={`languages[${index}].writing_level`}
                    label="مهارت نوشتن"
                    options={SUB_SKILL_OPTIONS}
                    disabled={isNative}
                    formik={formik}
                  />
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <PremiumSelect
                    name={`languages[${index}].speaking_level`}
                    label="مهارت مکالمه"
                    options={SUB_SKILL_OPTIONS}
                    disabled={isNative}
                    formik={formik}
                  />
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <PremiumSelect
                    name={`languages[${index}].listening_level`}
                    label="مهارت شنیداری"
                    options={SUB_SKILL_OPTIONS}
                    disabled={isNative}
                    formik={formik}
                  />
                </Grid>

                {isNative && (
                  <Grid size={{ xs: 12 }}>
                    <Typography
                      sx={{
                        color: "#00ffcc",
                        fontSize: 13,
                        fontStyle: "italic",
                        p: 1.5,
                        bgcolor: "rgba(0,255,204,0.05)",
                        borderRadius: 1,
                        border: "1px solid rgba(0,255,204,0.15)",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <span style={{ fontSize: 18 }}>💡</span>
                      برای زبان مادری، نیازی به تعیین مهارت‌های جداگانه نیست
                    </Typography>
                  </Grid>
                )}

                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(lang?.has_certificate)}
                        onChange={(e) =>
                          formik?.setFieldValue(
                            `languages[${index}].has_certificate`,
                            Boolean(e?.target?.checked),
                          )
                        }
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#00ffcc",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                            {
                              backgroundColor: "#00ffcc",
                            },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ color: "rgba(255,255,255,0.7)" }}>
                        دارای مدرک معتبر
                      </Typography>
                    }
                  />
                </Grid>

                {lang?.has_certificate && (
                  <>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <PremiumInput
                        name={`languages[${index}].certificate_name`}
                        label="نام مدرک"
                        formik={formik}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <PremiumInput
                        name={`languages[${index}].certificate_score`}
                        label="نمره / گرید"
                        formik={formik}
                      />
                    </Grid>
                  </>
                )}
              </Grid>

              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                <IconButton
                  onClick={() => onRemove?.(index)}
                  color="error"
                  sx={{
                    border: "1px solid rgba(244, 67, 54, 0.5)",
                    borderRadius: "8px",
                    "&:hover": { bgcolor: "rgba(244, 67, 54, 0.1)" },
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
          );
        })
      )}

      <Button
        variant="outlined"
        startIcon={<AddIcon sx={{ ml: "8px" }} />}
        onClick={onAdd}
        sx={{
          color: "#00ffcc",
          borderColor: "rgba(0,255,204,0.5)",
          borderRadius: "12px",
          "&:hover": {
            bgcolor: "rgba(0,255,204,0.1)",
            borderColor: "#00ffcc",
          },
        }}
      >
        افزودن زبان
      </Button>
    </Box>
  );
}
